import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search, Filter, Layers, ChevronRight } from 'lucide-react';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function MaterialCard({ material, rank }) {
  return (
    <Link 
      to={`/materials/${material.material_id}`}
      className="group bg-white border border-neutral-200 overflow-hidden hover:shadow-lg transition-all"
      data-testid={`similar-material-${material.material_id}`}
    >
      <div className="aspect-square bg-neutral-100 relative overflow-hidden">
        {material.images?.[0] ? (
          <img 
            src={material.images[0]} 
            alt={material.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-300">
            <Layers className="w-12 h-12" />
          </div>
        )}
        {rank && (
          <div className="absolute top-2 left-2 bg-neutral-900 text-white text-xs px-2 py-1">
            #{rank} Match
          </div>
        )}
        {material.similarity_score && (
          <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm text-xs px-2 py-1">
            {material.similarity_score}% similar
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-medium text-neutral-900 group-hover:text-neutral-600 transition-colors">
          {material.name}
        </h3>
        <p className="text-sm text-neutral-500 mt-1">
          {material.stone_type?.replace('_', ' ')} • {material.color}
        </p>
        {material.origin && (
          <p className="text-xs text-neutral-400 mt-1">{material.origin}</p>
        )}
      </div>
    </Link>
  );
}

export default function SimilarMaterialsPage() {
  const { materialId, slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchSimilarMaterials = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let endpoint = '';
      if (materialId) {
        endpoint = `${API}/similar/to/${materialId}`;
      } else if (slug) {
        endpoint = `${API}/similar/by-slug/${slug}`;
      } else {
        throw new Error('No material identifier provided');
      }
      
      const response = await axios.get(endpoint);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load similar materials');
    } finally {
      setLoading(false);
    }
  }, [materialId, slug]);
  
  useEffect(() => {
    fetchSimilarMaterials();
  }, [fetchSimilarMaterials]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 flex justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-neutral-900 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/materials')}
            className="text-neutral-600 hover:text-neutral-900"
          >
            Browse all materials
          </button>
        </div>
      </div>
    );
  }
  
  const { source_material, similar_materials, seo_title, seo_description } = data || {};
  
  return (
    <div className="min-h-screen bg-neutral-50" data-testid="similar-materials-page">
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-neutral-500">
            <Link to="/materials" className="hover:text-neutral-900">Materials</Link>
            <ChevronRight className="w-4 h-4" />
            {source_material && (
              <>
                <Link to={`/materials/${source_material.material_id}`} className="hover:text-neutral-900">
                  {source_material.name}
                </Link>
                <ChevronRight className="w-4 h-4" />
              </>
            )}
            <span className="text-neutral-900">Similar Materials</span>
          </nav>
        </div>
      </div>
      
      {/* Hero Section with Source Material */}
      {source_material && (
        <div className="bg-white border-b border-neutral-200">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              {/* Source Material Image */}
              <div className="w-full md:w-64 flex-shrink-0">
                <div className="aspect-square bg-neutral-100 overflow-hidden">
                  {source_material.images?.[0] ? (
                    <img 
                      src={source_material.images[0]} 
                      alt={source_material.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Layers className="w-16 h-16 text-neutral-300" />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1">
                <p className="text-sm text-neutral-500 uppercase tracking-wider mb-2">
                  Materials Similar To
                </p>
                <h1 className="text-3xl md:text-4xl font-light text-neutral-900 mb-4">
                  {source_material.name}
                </h1>
                <p className="text-neutral-600 mb-4">
                  {seo_description || `Discover materials with similar characteristics to ${source_material.name}.`}
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="bg-neutral-100 px-3 py-1 text-sm">
                    {source_material.stone_type?.replace('_', ' ')}
                  </span>
                  <span className="bg-neutral-100 px-3 py-1 text-sm">
                    {source_material.color}
                  </span>
                  {source_material.origin && (
                    <span className="bg-neutral-100 px-3 py-1 text-sm">
                      {source_material.origin}
                    </span>
                  )}
                </div>
                
                <div className="mt-6 flex gap-4">
                  <Link
                    to={`/materials/${source_material.material_id}`}
                    className="text-sm bg-neutral-900 text-white px-4 py-2 hover:bg-neutral-800"
                  >
                    View {source_material.name}
                  </Link>
                  <Link
                    to={`/compare?ids=${source_material.material_id}`}
                    className="text-sm border border-neutral-300 px-4 py-2 hover:bg-neutral-50"
                  >
                    Add to Compare
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Similar Materials Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-light">
            {similar_materials?.length || 0} Similar Materials Found
          </h2>
        </div>
        
        {similar_materials && similar_materials.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {similar_materials.map((material, index) => (
              <MaterialCard 
                key={material.material_id} 
                material={material} 
                rank={index + 1}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Layers className="w-16 h-16 mx-auto text-neutral-300 mb-4" />
            <p className="text-neutral-500">No similar materials found</p>
          </div>
        )}
        
        {/* Quick Compare CTA */}
        {similar_materials && similar_materials.length >= 2 && (
          <div className="mt-12 bg-neutral-100 p-8 text-center">
            <h3 className="text-xl font-light mb-3">Compare Materials Side by Side</h3>
            <p className="text-neutral-600 mb-6">
              Select up to 4 materials to compare specifications, durability, and maintenance requirements.
            </p>
            <Link
              to={`/compare?ids=${source_material?.material_id},${similar_materials[0]?.material_id}`}
              className="inline-block bg-neutral-900 text-white px-6 py-3 hover:bg-neutral-800"
            >
              Start Comparison
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
